import React, { useState, lazy, Suspense } from 'react';
import { ChevronRight, Search, BookOpen, FileText, Home, HelpCircle, ShoppingCart, Mail, Info, Settings, Users, Lock, Globe, Thermometer, Waves, Building2, Calendar, Layers, Rocket, Compass, Snowflake, Flame } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

// Lazy load all page components
const Landing = lazy(() => import('@/pages/Landing'));
const HowItWorks = lazy(() => import('@/pages/HowItWorks'));
const Pricing = lazy(() => import('@/pages/Pricing'));
const Contact = lazy(() => import('@/pages/Contact'));
const Blog = lazy(() => import('@/pages/Blog'));
const HelpBook = lazy(() => import('@/pages/HelpBook'));
const SaunaSense = lazy(() => import('@/pages/SaunaSense'));
const TermsAndConditions = lazy(() => import('@/pages/TermsAndConditions'));
const PrivacyPolicy = lazy(() => import('@/pages/PrivacyPolicy'));
const CookiePolicy = lazy(() => import('@/pages/CookiePolicy'));
const AcceptableUsePolicy = lazy(() => import('@/pages/AcceptableUsePolicy'));
const WhyKit = lazy(() => import('@/pages/WhyKit'));
const ProjectIntro = lazy(() => import('@/pages/ProjectIntro'));
const Directory = lazy(() => import('@/pages/Directory'));
const OperatorAdmin = lazy(() => import('@/pages/OperatorAdmin'));
const OperatorOnboarding = lazy(() => import('@/pages/OperatorOnboarding'));
const SaunaWizardPage = lazy(() => import('@/pages/SaunaWizardPage'));
const DesignSystem = lazy(() => import('@/pages/DesignSystem'));
const MaintenancePage = lazy(() => import('@/pages/MaintenancePage'));
const AllAuthFormsDemo = lazy(() => import('@/pages/AllAuthFormsDemo'));

interface ContentPage {
  id: string;
  title: string;
  path: string;
  icon: React.ReactNode;
  category: 'main' | 'legal' | 'marketing' | 'admin' | 'help' | 'design' | 'auth' | 'wizard';
  component: React.LazyExoticComponent<React.FC>;
}

const contentPages: ContentPage[] = [
  // Main Pages
  { id: 'landing', title: 'Landing Page', path: '/', icon: <Home className="w-5 h-5" />, category: 'main', component: Landing },
  { id: 'how-it-works', title: 'How It Works', path: '/how-it-works', icon: <Info className="w-5 h-5" />, category: 'main', component: HowItWorks },
  { id: 'pricing', title: 'Pricing', path: '/pricing', icon: <ShoppingCart className="w-5 h-5" />, category: 'main', component: Pricing },
  { id: 'contact', title: 'Contact', path: '/contact', icon: <Mail className="w-5 h-5" />, category: 'main', component: Contact },
  { id: 'blog', title: 'Blog', path: '/blog', icon: <FileText className="w-5 h-5" />, category: 'main', component: Blog },
  
  // Help & Support
  { id: 'help-book', title: 'Help Book', path: '/help-book', icon: <BookOpen className="w-5 h-5" />, category: 'help', component: HelpBook },
  { id: 'sauna-sense', title: 'SaunaSense', path: '/sauna-sense', icon: <Thermometer className="w-5 h-5" />, category: 'help', component: SaunaSense },
  
  // Legal Pages
  { id: 'terms', title: 'Terms & Conditions', path: '/terms', icon: <FileText className="w-5 h-5" />, category: 'legal', component: TermsAndConditions },
  { id: 'privacy', title: 'Privacy Policy', path: '/privacy', icon: <FileText className="w-5 h-5" />, category: 'legal', component: PrivacyPolicy },
  { id: 'cookie', title: 'Cookie Policy', path: '/cookie-policy', icon: <FileText className="w-5 h-5" />, category: 'legal', component: CookiePolicy },
  { id: 'acceptable-use', title: 'Acceptable Use Policy', path: '/acceptable-use', icon: <FileText className="w-5 h-5" />, category: 'legal', component: AcceptableUsePolicy },
  
  // Marketing Pages
  { id: 'why-kit', title: 'Why Kit', path: '/why-kit', icon: <Info className="w-5 h-5" />, category: 'marketing', component: WhyKit },
  { id: 'project-intro', title: 'Project Intro', path: '/project-intro', icon: <Rocket className="w-5 h-5" />, category: 'marketing', component: ProjectIntro },
  { id: 'directory', title: 'Directory', path: '/directory', icon: <Compass className="w-5 h-5" />, category: 'marketing', component: Directory },
  
  // Admin Pages
  { id: 'operator-admin', title: 'Operator Admin', path: '/operator-admin', icon: <Settings className="w-5 h-5" />, category: 'admin', component: OperatorAdmin },
  { id: 'operator-onboarding', title: 'Operator Onboarding', path: '/operator-onboarding', icon: <Users className="w-5 h-5" />, category: 'admin', component: OperatorOnboarding },
  
  // Design System
  { id: 'design-system', title: 'Design System', path: '/design-system', icon: <Layers className="w-5 h-5" />, category: 'design', component: DesignSystem },
  
  // Auth Pages
  { id: 'auth-forms', title: 'Auth Forms Demo', path: '/auth-forms', icon: <Lock className="w-5 h-5" />, category: 'auth', component: AllAuthFormsDemo },
  
  // Wizard Pages
  { id: 'wizard', title: 'Listing Wizard', path: '/sauna-wizard', icon: <Building2 className="w-5 h-5" />, category: 'wizard', component: SaunaWizardPage },
];

const ContentReview: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | 'all'>('all');
  const [selectedPage, setSelectedPage] = useState<ContentPage | null>(null);

  const filteredPages = contentPages.filter(page => {
    const matchesSearch = page.title.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || page.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const categories = [
    { id: 'all', label: 'All Pages' },
    { id: 'main', label: 'Main Pages' },
    { id: 'help', label: 'Help & Support' },
    { id: 'legal', label: 'Legal' },
    { id: 'marketing', label: 'Marketing' },
    { id: 'admin', label: 'Admin' },
    { id: 'design', label: 'Design System' },
    { id: 'auth', label: 'Auth Forms' },
    { id: 'wizard', label: 'Wizards' },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Content Review Dashboard | SaunaDip</title>
        <meta name="description" content="Review and manage all site content in one place" />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Content Review Dashboard</h1>
          <p className="text-gray-600">Review and manage all site content in one place</p>
        </div>

        {/* Search and Filter */}
        <div className="mb-8 flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search pages..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-2">
            {categories.map(category => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id as string | 'all')}
                className={`px-4 py-2 rounded-lg whitespace-nowrap ${
                  selectedCategory === category.id
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                {category.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredPages.map(page => (
            <button
              key={page.id}
              onClick={() => setSelectedPage(page)}
              className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow text-left border border-gray-200"
            >
              <div className="flex items-start gap-4">
                <div className="p-2 bg-blue-50 rounded-lg text-blue-600">
                  {page.icon}
                </div>
                <div>
                  <h3 className="font-medium text-gray-900 mb-1">{page.title}</h3>
                  <p className="text-sm text-gray-500">{page.path}</p>
                  <div className="mt-2">
                    <span className="inline-block px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
                      {page.category}
                    </span>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400 ml-auto" />
              </div>
            </button>
          ))}
        </div>

        {/* Selected Page Preview */}
        {selectedPage && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl max-w-7xl w-full max-h-[90vh] overflow-hidden flex flex-col">
              <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-2 bg-blue-50 rounded-lg text-blue-600">
                    {selectedPage.icon}
                  </div>
                  <div>
                    <h2 className="text-xl font-medium text-gray-900">{selectedPage.title}</h2>
                    <p className="text-sm text-gray-500">{selectedPage.path}</p>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedPage(null)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <span className="sr-only">Close</span>
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="p-6 overflow-y-auto flex-1">
                <Suspense fallback={
                  <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                  </div>
                }>
                  <div className="prose max-w-none">
                    <selectedPage.component />
                  </div>
                </Suspense>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContentReview; 